import { useState, type ReactNode } from "react";

import { Banner, Button, Checkbox, Sheet, Textarea } from "../../../shared/ui";
import { controlClassName } from "../../../shared/ui/controlClassName";
import {
  OperatorDenied,
  OperatorRefused,
  STEP_UP_REQUIRED,
} from "../operatorHttp";
import { isSessionExpired, useReportSessionExpired } from "../sessionContext";
import {
  MAX_REASON_CHARS,
  REASON_CODES,
  type AccessChange,
  type MutationResult,
} from "./accessApi";
import type { AccessEnv } from "./accessEnv";
import { errorMessage } from "./accessFormat";
import { StepUpNotice } from "./StepUpNotice";
import { fill, useAccessT } from "./useAccessT";

/**
 * Confirmation sheet shared by every access mutation: consequences warning,
 * reason code, free-text reason and an explicit confirmation. Rendered only
 * while open, so its draft state is fresh each time. Server refusals map to
 * specific messages; step_up_required swaps the form for the sign-in notice.
 */
export function ChangeReasonSheet({
  env,
  title,
  summary,
  warning,
  extraValid = true,
  children,
  run,
  onClose,
}: {
  env: AccessEnv;
  title: string;
  summary?: string;
  warning: string;
  /** Validity of the caller's own fields (add-operator form). */
  extraValid?: boolean;
  children?: ReactNode;
  run: (change: AccessChange) => Promise<MutationResult>;
  onClose: () => void;
}) {
  const t = useAccessT();
  const reportExpired = useReportSessionExpired();
  const [code, setCode] = useState("");
  const [reason, setReason] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stepUp, setStepUp] = useState(false);

  const trimmed = reason.trim();
  const valid =
    extraValid &&
    code !== "" &&
    trimmed !== "" &&
    trimmed.length <= MAX_REASON_CHARS &&
    confirmed;

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await run({
        reason_code: code,
        reason: trimmed,
        confirm: true,
      });
      onClose();
      env.onChanged(result);
    } catch (failure: unknown) {
      if (isSessionExpired(failure)) {
        reportExpired();
      } else if (
        failure instanceof OperatorRefused &&
        failure.code === STEP_UP_REQUIRED
      ) {
        setStepUp(true);
      } else if (failure instanceof OperatorDenied) {
        env.onDenied();
        setError(t("admin.access.error.denied"));
      } else if (failure instanceof OperatorRefused) {
        setError(errorMessage(t, failure.code));
      } else {
        setError(t("admin.access.error.generic"));
      }
      setBusy(false);
    }
  };

  return (
    <Sheet
      open
      onOpenChange={(open) => {
        if (!open && !busy) onClose();
      }}
      title={title}
      description={summary}
    >
      {stepUp ? (
        <StepUpNotice
          onCancel={onClose}
          onReauthenticate={async () => {
            await env.api.reauthenticate();
            env.onSignIn();
          }}
        />
      ) : (
        <form
          className="flex flex-col gap-3"
          onSubmit={(event) => {
            event.preventDefault();
            if (valid && !busy) void submit();
          }}
        >
          <Banner tone="warning">{warning}</Banner>
          {children}
          <label className="flex min-w-0 flex-col gap-1 text-footnote text-fg-muted">
            {t("admin.access.reason.code")}
            <select
              className={controlClassName}
              value={code}
              onChange={(event) => {
                setCode(event.target.value);
              }}
            >
              <option value="">
                {t("admin.access.reason.code.placeholder")}
              </option>
              {REASON_CODES.map((id) => (
                <option key={id} value={id}>
                  {t(`admin.access.reason.code.${id}`)}
                </option>
              ))}
            </select>
          </label>
          <label className="flex min-w-0 flex-col gap-1 text-footnote text-fg-muted">
            {t("admin.access.reason.text")}
            <Textarea
              value={reason}
              maxLength={MAX_REASON_CHARS}
              onChange={(event) => {
                setReason(event.target.value);
              }}
            />
            <span className="self-end text-caption">
              {fill(t("admin.access.reason.counter"), {
                n: String(reason.length),
                max: String(MAX_REASON_CHARS),
              })}
            </span>
          </label>
          <Checkbox
            checked={confirmed}
            onCheckedChange={setConfirmed}
            label={t("admin.access.reason.confirm")}
          />
          {error !== null && (
            <p role="alert" className="text-footnote text-error">
              {error}
            </p>
          )}
          <div className="flex flex-wrap justify-end gap-2.5">
            <Button type="button" variant="ghost" onClick={onClose}>
              {t("admin.access.reason.cancel")}
            </Button>
            <Button type="submit" variant="danger" disabled={!valid || busy}>
              {busy
                ? t("admin.access.reason.pending")
                : t("admin.access.reason.submit")}
            </Button>
          </div>
        </form>
      )}
    </Sheet>
  );
}
